import { useEffect, useRef } from 'react';
import { useSoftphone } from '@/store/softphone';
import { useAudio } from '@/store/audio';

/**
 * Global audio sink for the active call's remote stream.
 *
 * Mount ONCE in AppShell — not inside ActiveCallCard. Otherwise calls placed
 * from the Topbar (or any non-active-calls route) attach the remote track to
 * a detached audio element and the user hears silence even though SRTP is
 * flowing (bytesRecv > 0). Audio routing must be independent of which page
 * is currently rendered.
 */
export function CallAudio() {
  const remoteStream = useSoftphone((s) => s.remoteStream);
  const speakerDeviceId = useAudio((s) => s.speakerDeviceId);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    console.log('[call-audio] remoteStream=', remoteStream?.id ?? null,
      'tracks=', remoteStream?.getTracks().length ?? 0);
    el.srcObject = remoteStream;
    if (remoteStream) {
      el.play()
        .then(() => console.log('[call-audio] play() ok, paused=', el.paused, 'volume=', el.volume))
        .catch((e) => console.warn('[call-audio] play() blocked:', e.name, e.message));
    }
    return () => {
      if (audioRef.current) audioRef.current.srcObject = null;
    };
  }, [remoteStream]);

  useEffect(() => {
    const el = audioRef.current as (HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> }) | null;
    if (!el || !el.setSinkId || speakerDeviceId === 'default') return;
    el.setSinkId(speakerDeviceId).catch((e) => console.warn('[call-audio] setSinkId failed', e));
  }, [speakerDeviceId, remoteStream]);

  return <audio ref={audioRef} autoPlay playsInline className="hidden" />;
}
